import { useState } from 'react';
import { Box, Button, Flex, Image, Stack, Text } from '@chakra-ui/react';
import { Plus } from '@phosphor-icons/react';

import aiIcon from '@/assets/icon/ai-icon.svg';

import {
  AlertsAndFollowUpQuestion,
  ResumeAnalysisResult as ResumeAnalysisResultType
} from '@/types/AnalysisResult';
import { FollowUpQuestionStatus } from '@/types/enums';

import { RequirementMatchCollapsible, FollowUpItemBox } from '@/views/Candidate/components';

export default function ResumeAnalysisResult({
  analysisResult,
  followUpQuestions,
  onUpdateItem = async () => true,
  editable = false,
  followUpItemsRef
}: {
  analysisResult: ResumeAnalysisResultType;
  followUpQuestions: AlertsAndFollowUpQuestion[];
  onUpdateItem?: (index: number, item: AlertsAndFollowUpQuestion | null) => Promise<boolean>;
  editable?: boolean;
  followUpItemsRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const [alignedItemsOpen, setAlignedItemsOpen] = useState(true);
  const [gapsOpen, setGapsOpen] = useState(true);
  const [followUpItems, setFollowUpItems] = useState(followUpQuestions);

  const addFollowUpItem = () => {
    setFollowUpItems((prevItems) => [
      ...prevItems,
      {
        title: '',
        alert: '',
        follow_up_questions: [{ question: '', question_type: 'essay_question' }],
        status: FollowUpQuestionStatus.Pending,
        feedback: '',
        related_interview_content_titles: [],
        resolved: false
      }
    ]);
  };

  const updateFollowUpItem = async (
    index: number,
    item: AlertsAndFollowUpQuestion | null
  ): Promise<boolean> => {
    const updatedItems = [...followUpItems];

    if (item === null) {
      const removedItem = updatedItems[index];
      updatedItems.splice(index, 1);

      if (removedItem.title === '') {
        setFollowUpItems(updatedItems);
        return true;
      }
    } else {
      updatedItems[index] = item;
    }

    const success = await onUpdateItem(index, item);
    if (success) {
      setFollowUpItems(updatedItems);
    }

    return success;
  };

  return (
    <Flex direction="column" w="100%" align="flex-start">
      <Flex alignItems="center" mb={{ base: '8px', md: '12px' }}>
        <Image src={aiIcon} w={{ base: '16px', md: '20px' }} h={{ base: '16px', md: '20px' }} />
        <Box
          color="neutrals.900"
          fontSize={{ base: '16px', md: '18px' }}
          fontWeight="semibold"
          ml={{ base: '6px', md: '8px' }}
        >
          AI Overview
        </Box>
      </Flex>
      <Text
        id="ai-overview"
        fontSize={{ base: '14px', md: '16px' }}
        color="neutrals.700"
        p={0}
        mb={{ base: '24px', md: '32px' }}
        whiteSpace="pre-wrap"
        lineHeight="1.5"
      >
        {analysisResult.overview}
      </Text>
      <Text
        color="neutrals.900"
        fontSize={{ base: '20px', md: '24px' }}
        fontWeight="semibold"
        mb={{ base: '6px', md: '8px' }}
      >
        Requirements Match
      </Text>
      <Text
        fontSize={{ base: '14px', md: '16px' }}
        color="neutrals.700"
        mb={{ base: '16px', md: '24px' }}
        lineHeight="1.5"
      >
        This section compares the candidate's resume with the job description to highlight aligned
        and misaligned skills for quick suitability assessment.
      </Text>
      <RequirementMatchCollapsible
        title="Aligned Items"
        matchType="aligned"
        items={analysisResult.aligned_skills}
        isOpen={alignedItemsOpen}
        setOpen={setAlignedItemsOpen}
      />
      <RequirementMatchCollapsible
        title="Misaligned Items"
        matchType="misaligned"
        items={analysisResult.gaps_or_misalignments}
        isOpen={gapsOpen}
        setOpen={setGapsOpen}
      />
      <Box ref={followUpItemsRef}>
        <Text
          color="neutrals.900"
          fontSize={{ base: '20px', md: '24px' }}
          fontWeight="semibold"
          mb={{ base: '6px', md: '8px' }}
        >
          Follow-up Items
        </Text>
        <Text
          fontSize={{ base: '14px', md: '16px' }}
          color="neutrals.700"
          mb={{ base: '16px', md: '24px' }}
          lineHeight="1.5"
        >
          This section highlights areas requiring clarification or action to better evaluate the
          candidate's fit for the role, focusing on gaps or ambiguities identified during the
          initial review.
        </Text>
        <Stack gap={{ base: '16px', md: '24px' }} w="100%">
          {followUpItems.map((item, index) => (
            <FollowUpItemBox
              key={index}
              followUpItem={item}
              index={index}
              onUpdateItem={updateFollowUpItem}
              readonly={!editable}
            />
          ))}
        </Stack>
      </Box>
      {editable && (
        <Button
          variant="ghost"
          color="primary.900"
          fontSize={{ base: '14px', md: '16px' }}
          fontWeight="semibold"
          _hover={{ bg: 'transparent' }}
          mt={{ base: '16px', md: '24px' }}
          onClick={addFollowUpItem}
          w={{ base: '100%', sm: 'auto' }}
          justifyContent={{ base: 'center', sm: 'flex-start' }}
          px={{ base: '16px', md: '0' }}
        >
          <Plus size={24} weight="bold" />
          <Box ml="8px">Create a new follow-up item</Box>
        </Button>
      )}
    </Flex>
  );
}
